import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import {
    Users,
    UserPlus,
    Crown,
    Shield,
    Edit3,
    Eye,
    Loader2,
    Trash2,
    Mail,
} from "lucide-react";

const MEMBER_ROLES = [
    { value: "owner", label: "Propriétaire", icon: Crown, color: "bg-amber-500", description: "Contrôle total" },
    { value: "manager", label: "Manager", icon: Shield, color: "bg-violet-500", description: "Peut gérer l'équipe" },
    { value: "editor", label: "Éditeur", icon: Edit3, color: "bg-blue-500", description: "Peut modifier" },
    { value: "viewer", label: "Lecteur", icon: Eye, color: "bg-gray-500", description: "Lecture seule" },
];

const getRoleInfo = (role) => MEMBER_ROLES.find((r) => r.value === role) || MEMBER_ROLES[3];

export const ProjectMembersTab = ({ projectId, isOwner = false, isAdmin = false }) => {
    const { get, post, put, del } = useApi();
    const [members, setMembers] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addDialog, setAddDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(null);
    const [removeDialog, setRemoveDialog] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedUser, setSelectedUser] = useState("");
    const [selectedRole, setSelectedRole] = useState("editor");

    const canManage = isOwner || isAdmin;

    const fetchMembers = useCallback(async () => {
        try {
            const res = await get(`/projects/${projectId}/members`);
            setMembers(res || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [get, projectId]);

    const fetchAvailableUsers = useCallback(async () => {
        try {
            const users = await get("/users");
            // Filter out users who are already members
            const memberIds = members.map((m) => m.user_id);
            setAvailableUsers(users.filter((u) => !memberIds.includes(u.user_id)));
        } catch (error) {
            console.error(error);
        }
    }, [get, members]);

    useEffect(() => { fetchMembers(); }, [fetchMembers]);

    useEffect(() => {
        if (addDialog) {
            fetchAvailableUsers();
        }
    }, [addDialog, fetchAvailableUsers]);

    const handleAddMember = async () => {
        if (!selectedUser) {
            toast.error("Sélectionnez un utilisateur");
            return;
        }
        setSaving(true);
        try {
            await post(`/projects/${projectId}/members`, {
                user_id: selectedUser,
                role: selectedRole,
            });
            toast.success("Membre ajouté au projet");
            setAddDialog(false);
            setSelectedUser("");
            setSelectedRole("editor");
            fetchMembers();
        } catch (error) {
            toast.error(error.message || "Erreur lors de l'ajout");
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateMember = async () => {
        if (!editDialog) return;
        setSaving(true);
        try {
            await put(`/projects/${projectId}/members/${editDialog.user_id}`, {
                role: editDialog.newRole,
            });
            toast.success("Permissions mises à jour");
            setEditDialog(null);
            fetchMembers();
        } catch (error) {
            toast.error(error.message || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveMember = async () => {
        if (!removeDialog) return;
        setSaving(true);
        try {
            await del(`/projects/${projectId}/members/${removeDialog.user_id}`);
            toast.success("Membre retiré du projet");
            setRemoveDialog(null);
            fetchMembers();
        } catch (error) {
            toast.error(error.message || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="project-members-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-heading font-semibold text-lg">Membres du projet</h3>
                    <p className="text-sm text-muted-foreground">
                        {members.length} membre{members.length > 1 ? "s" : ""}
                    </p>
                </div>
                {canManage && (
                    <Button onClick={() => setAddDialog(true)} size="sm" data-testid="add-member-btn">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Ajouter
                    </Button>
                )}
            </div>

            {/* Role Legend */}
            <div className="flex flex-wrap gap-2">
                {MEMBER_ROLES.map((role) => {
                    const Icon = role.icon;
                    return (
                        <Badge key={role.value} variant="outline" className="gap-1">
                            <Icon className="w-3 h-3" />
                            <span>{role.label}</span>
                        </Badge>
                    );
                })}
            </div>

            {/* Members List */}
            <div className="space-y-2">
                {members.map((member) => {
                    const roleInfo = getRoleInfo(member.role);
                    const RoleIcon = roleInfo.icon;
                    return (
                        <div
                            key={member.user_id}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                    <span className="text-sm font-medium text-primary">
                                        {member.user_name?.charAt(0)?.toUpperCase() || "?"}
                                    </span>
                                </div>
                                <div>
                                    <p className="font-medium">{member.user_name || "Inconnu"}</p>
                                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                                        <Mail className="w-3 h-3" />
                                        {member.user_email}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge className={`${roleInfo.color} gap-1`}>
                                    <RoleIcon className="w-3 h-3" />
                                    {roleInfo.label}
                                </Badge>
                                {canManage && member.role !== "owner" && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setEditDialog({ ...member, newRole: member.role })}
                                        >
                                            <Edit3 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setRemoveDialog(member)}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
                {members.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Aucun membre dans ce projet</p>
                    </div>
                )}
            </div>

            {/* Add Member Dialog */}
            <Dialog open={addDialog} onOpenChange={setAddDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Ajouter un membre</DialogTitle>
                        <DialogDescription>
                            Sélectionnez un utilisateur et définissez son niveau d'accès
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Utilisateur</label>
                            <Select value={selectedUser} onValueChange={setSelectedUser}>
                                <SelectTrigger data-testid="select-user">
                                    <SelectValue placeholder="Sélectionner un utilisateur" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableUsers.map((user) => (
                                        <SelectItem key={user.user_id} value={user.user_id}>
                                            {user.name} ({user.email})
                                        </SelectItem>
                                    ))}
                                    {availableUsers.length === 0 && (
                                        <SelectItem value="" disabled>
                                            Tous les utilisateurs sont déjà membres
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-medium">Niveau d'accès</label>
                            <Select value={selectedRole} onValueChange={setSelectedRole}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MEMBER_ROLES.filter((r) => r.value !== "owner").map((role) => (
                                        <SelectItem key={role.value} value={role.value}>
                                            <div className="flex items-center gap-2">
                                                <role.icon className="w-4 h-4" />
                                                {role.label} - {role.description}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleAddMember} disabled={saving || !selectedUser} data-testid="confirm-add-member">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Ajouter
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Role Dialog */}
            <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Modifier les permissions</DialogTitle>
                        <DialogDescription>
                            Changer le niveau d'accès de {editDialog?.user_name}
                        </DialogDescription>
                    </DialogHeader>
                    {editDialog && (
                        <div>
                            <label className="text-sm font-medium">Nouveau niveau d'accès</label>
                            <Select
                                value={editDialog.newRole}
                                onValueChange={(v) => setEditDialog({ ...editDialog, newRole: v })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MEMBER_ROLES.filter((r) => r.value !== "owner").map((role) => (
                                        <SelectItem key={role.value} value={role.value}>
                                            {role.label} - {role.description}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialog(null)}>
                            Annuler
                        </Button>
                        <Button onClick={handleUpdateMember} disabled={saving}>
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Remove Confirmation */}
            <AlertDialog open={!!removeDialog} onOpenChange={() => setRemoveDialog(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Retirer {removeDialog?.user_name} ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette personne n'aura plus accès à ce projet.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRemoveMember} className="bg-destructive text-destructive-foreground">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Retirer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
